import { PriceUtils } from '@wix/bookings-uou-mappers';
import { useTranslation } from '@wix/yoshi-flow-editor';
import React from 'react';
import { Divider } from 'wix-ui-tpa/cssVars';
import { ServicePaymentDetails } from '../../../../../types/types';
import { TagName } from '../../../../../utils/tagName.const';
import Text, { TextType } from '../../../Text/Text';
import { PaymentSummaryDataHook } from '../dataHooks';
import { classes as parentClasses } from '../PaymentSummary.st.css';
import { classes } from './SubscriptionSummary.st.css';
import { PriceItem } from '../PriceItem/PriceItem';
import type { SubscriptionDiscountDetails } from '../../../../../utils/mappers/serviceMapper/utils/payment.mapper';
import { SubscriptionDiscounts } from './SubscriptionDiscounts';
import {
  ProductAddonLine,
  getProductAddonsTotal,
} from '../getProductAddonLines';

type SubscriptionSummaryProps = {
  price: number;
  currency: string;
  locale: string;
  paymentDetails: ServicePaymentDetails;
  numberOfParticipants: number;
  productAddonLines: ProductAddonLine[];
  allowInlinePaymentNumber: boolean;
  isCart: boolean | undefined;
  discountDetails?: SubscriptionDiscountDetails;
  children?: React.ReactNode;
};

export const SubscriptionSummary: React.FC<SubscriptionSummaryProps> = ({
  price,
  currency,
  locale,
  paymentDetails,
  numberOfParticipants,
  productAddonLines,
  allowInlinePaymentNumber,
  isCart,
  discountDetails,
  children,
}) => {
  const { t } = useTranslation();

  const baseEnrollmentFee = paymentDetails.subscriptionInfo?.enrollmentFeeAmount
    ? Number(paymentDetails.subscriptionInfo.enrollmentFeeAmount.value)
    : 0;
  const enrollmentFeeValue = baseEnrollmentFee * numberOfParticipants;

  const info = paymentDetails.subscriptionInfo;
  const pricePerParticipant = discountDetails
    ? discountDetails.pricePerParticipant
    : price;
  const monthlyPrice = discountDetails?.price ?? price * numberOfParticipants;
  const hasProductAddons = productAddonLines.length > 0;
  const productAddonsTotal = getProductAddonsTotal(productAddonLines);
  const subtotal = monthlyPrice + productAddonsTotal;
  const formattedSubtotal = PriceUtils.getFormattedCurrency({
    price: subtotal,
    currency,
    locale,
  });
  const formattedPricePerParticipant = PriceUtils.getFormattedCurrency({
    price: pricePerParticipant ?? 0,
    currency,
    locale,
  });

  const formattedMonthlyPricePerParticipant = t(
    'app.payment.summary.subscription.monthly-price',
    { price: formattedPricePerParticipant },
  );
  const showParticipantPrice = numberOfParticipants > 1 || hasProductAddons;
  const showPaymentNumberWithParticipants =
    showParticipantPrice && allowInlinePaymentNumber;
  const paymentNumber = info
    ? t('app.payment.summary.subscription.payment-number', {
        current: 1,
        total: info.totalPayments,
      })
    : undefined;

  return (
    <div className={parentClasses.singleSession}>
      <div className={parentClasses.details}>
        {children}
        <div className={classes.subscriptionSubtotalGroup}>
          <PriceItem
            type={TextType.Primary}
            labels={[t('app.payment.summary.subtotal.text')]}
            price={
              hasProductAddons
                ? formattedSubtotal
                : t('app.payment.summary.subscription.monthly-price', {
                    price: formattedSubtotal,
                  })
            }
            ariaPrice={PriceUtils.getFormattedCurrency({
              price: subtotal,
              currency,
              locale,
              currencyDisplay: 'name',
            })}
            // Compare the same subtotal before and after the monthly discount;
            // one-time products contribute equally to both amounts.
            priceBeforeDiscountText={
              discountDetails && !discountDetails.discountCalculatedAtCheckout
                ? PriceUtils.getFormattedCurrency({
                    price:
                      discountDetails.priceBeforeDiscount + productAddonsTotal,
                    currency,
                    locale,
                  })
                : undefined
            }
            dataHook={PaymentSummaryDataHook.Subscription_Price}
            isCart={isCart}
          />
          <div className={classes.subscriptionInfo}>
            {showParticipantPrice && (
              <Text
                type={TextType.Secondary}
                tagName={TagName.Paragraph}
                data-hook={PaymentSummaryDataHook.Subscription_Details}
              >
                {pricePerParticipant === undefined
                  ? t('app.bookings-form.bookings-details.participants-count', {
                      count: numberOfParticipants,
                    })
                  : t('app.payment.summary.subscription.participants', {
                      count: numberOfParticipants,
                      price: formattedMonthlyPricePerParticipant,
                    })}
                {showPaymentNumberWithParticipants &&
                  paymentNumber &&
                  ` (${paymentNumber})`}
              </Text>
            )}
            {!showPaymentNumberWithParticipants && paymentNumber && (
              <Text type={TextType.Secondary} tagName={TagName.Paragraph}>
                {paymentNumber}
              </Text>
            )}
            {productAddonLines.map(({ key, name, quantity, unitPrice }) => (
              <Text
                key={key}
                type={TextType.Secondary}
                tagName={TagName.Paragraph}
                data-hook={PaymentSummaryDataHook.Product_Item}
              >
                {t('app.payment.summary.number-of-custom-choice.text', {
                  numberOfParticipants: quantity,
                  price: PriceUtils.getFormattedCurrency({
                    price: unitPrice,
                    currency,
                    locale,
                  }),
                  choice: name,
                })}
              </Text>
            ))}
          </div>
        </div>
        {paymentDetails.subscriptionInfo?.enrollmentFeeAmount && (
          <div className={classes.enrollmentFee}>
            <PriceItem
              type={TextType.Primary}
              labels={[t('app.payment.summary.enrollment-fee.label')]}
              price={PriceUtils.getFormattedCurrency({
                price: enrollmentFeeValue,
                currency,
                locale,
              })}
              ariaPrice={PriceUtils.getFormattedCurrency({
                price: enrollmentFeeValue,
                currency,
                locale,
                currencyDisplay: 'name',
              })}
              dataHook={PaymentSummaryDataHook.Enrollment_Fee}
              isCart={isCart}
            />
          </div>
        )}
        <Divider className={parentClasses.divider} />
        <PriceItem
          type={TextType.Primary}
          labels={[t('app.payment.summary.total-due-now.label')]}
          price={PriceUtils.getFormattedCurrency({
            price: subtotal + enrollmentFeeValue,
            currency,
            locale,
          })}
          ariaPrice={PriceUtils.getFormattedCurrency({
            price: subtotal + enrollmentFeeValue,
            currency,
            locale,
            currencyDisplay: 'name',
          })}
          dataHook={PaymentSummaryDataHook.Pay_Now}
          isCart={isCart}
        />
        {discountDetails && info && (
          <SubscriptionDiscounts
            discount={discountDetails.discount}
            calculatedAtCheckout={discountDetails.discountCalculatedAtCheckout}
            totalPayments={info.totalPayments}
            isCart={isCart}
          />
        )}
      </div>
    </div>
  );
};
